/**
 * useBuddyVisuals — lightweight client-side visual composer.
 * Takes backend-provided state + client-side transient and produces rendering parameters.
 *
 * This does NOT run any engine logic — it only maps backend data
 * to visual parameters. Transient reactions are managed separately
 * by useTransientReactions and passed in.
 */

import { BackendBuddyState } from '@/hooks/useBuddyBackend';
import { ShortTermBuddyMood } from '@/lib/types/mood';
import { TransientReactionType } from '@/lib/types/events';
import {
  composeBuddyState,
  ComposedBuddyState,
} from '@/lib/state/baselineMoodComposer';
import {
  applyPersonalityToBaseline,
  PERSONALITY_PROFILES,
  DEFAULT_PERSONALITY,
} from '@/lib/state/personalityEngine';
import { EnvironmentState } from '@/lib/state/environmentMoodComposer';
import { SignatureBehavior } from '@/lib/state/signatureBehaviorMap';
import { DEFAULT_SENTIMENT, SentimentData } from '@/lib/types/sentiment';

export interface BuddyVisualsOutput {
  composed: ComposedBuddyState;
  environment: EnvironmentState;
  heatLevel: number;
  activeSignature: SignatureBehavior | null;
}

const VALID_MOODS: ShortTermBuddyMood[] = [
  'calm', 'curious', 'alert', 'stressed', 'relieved', 'vibing', 'hyped', 'shaken', 'celebrating',
];

const VALID_SIGNATURES: SignatureBehavior[] = [
  'queueStare', 'minerPrayer', 'reliefExhale', 'crabDeadpan', 'laserFlex', 'feeFeverTwitch',
];

const DEFAULT_ENVIRONMENT: EnvironmentState = {
  particleDensity: 0.15,
  particleSpeed: 0.2,
  particleOpacity: 0.25,
  ambientBrightness: 0.5,
  ambientWarmth: 0.5,
  glowIntensity: 0.15,
  spaciousness: 0.8,
  tensionLevel: 0.05,
  stillness: 0.8,
  bgGradient: 'radial-gradient(ellipse at 50% 50%, hsl(30 95% 56% / 0.02) 0%, transparent 65%)',
  pulseFrequency: 6,
};

const personality = PERSONALITY_PROFILES[DEFAULT_PERSONALITY];

/**
 * Derive visual rendering parameters from backend state.
 * @param activeTransient - from useTransientReactions (client-side real-time events)
 */
export function useBuddyVisuals(
  bs: BackendBuddyState | null,
  activeTransient: TransientReactionType | null = null,
): BuddyVisualsOutput {
  if (!bs) {
    const defaultComposed: ComposedBuddyState = {
      baseline: {
        hoverHeight: 6, floatAmplitude: 0.6, pulseRate: 4.5, breathingDepth: 0.012,
        blinkInterval: 6000, blinkDuration: 300, doubleBlink: false,
        auraDensity: 0.2, auraBrightness: 0.7, auraTension: 0, auraColor: '--bitcoin',
        faceOpenness: 0.4, smileBias: 0.3, eyeTension: 0, postureBias: 0.5,
        txBubbleDensity: 0.15, txBubbleSpeed: 0.3, backgroundStillness: 0.9, colorWarmth: 0.7,
        gestureFrequency: 12000,
        gestureSet: ['longBlink', 'softSway', 'satisfiedBounce'],
      },
      shortTermMood: 'calm',
      activeTransient: null,
      longTermStatusLabel: 'Getting to know the network...',
    };
    return {
      composed: defaultComposed,
      environment: DEFAULT_ENVIRONMENT,
      heatLevel: 0,
      activeSignature: null,
    };
  }

  const shortTermMood: ShortTermBuddyMood = VALID_MOODS.includes(bs.buddyMood as any)
    ? (bs.buddyMood as ShortTermBuddyMood)
    : 'calm';

  // During a transient, suppress signature behaviors
  const activeSignature: SignatureBehavior | null = activeTransient
    ? null
    : (bs.activeSignature && VALID_SIGNATURES.includes(bs.activeSignature as any)
      ? (bs.activeSignature as SignatureBehavior)
      : null);

  const sentiment: SentimentData = bs.sentimentValue != null
    ? { value: bs.sentimentValue, bucket: bs.sentimentBucket as any ?? 'neutral', label: '', timestamp: 0 }
    : DEFAULT_SENTIMENT;

  const longTermState = {
    scores: bs.longTermScores,
    networkMood: bs.networkMood as any,
    marketMood: bs.marketMood as any,
    blockMood: bs.blockMood as any,
    confidence: bs.ltConfidence as any,
    statusLabel: bs.ltStatusLabel,
  };

  // Pass activeTransient so composeBuddyState includes it in output
  const composed = composeBuddyState(longTermState, shortTermMood, activeTransient, sentiment);

  const personalityMods = applyPersonalityToBaseline(composed.baseline, personality);
  const enhancedComposed: ComposedBuddyState = {
    ...composed,
    baseline: {
      ...composed.baseline,
      blinkInterval: personalityMods.blinkInterval,
      breathingDepth: personalityMods.breathingDepth,
      gestureFrequency: personalityMods.gestureFrequency,
      eyeTension: personalityMods.eyeTension,
      smileBias: personalityMods.smileBias,
      auraBrightness: personalityMods.auraBrightness,
    },
  };

  return {
    composed: enhancedComposed,
    environment: bs.environment,
    heatLevel: Number.isFinite(bs.heatLevel) ? Math.min(1, Math.max(0, bs.heatLevel)) : 0,
    activeSignature,
  };
}
